import { Controller } from "@hotwired/stimulus";
import {
  bindAnchoredPanelReposition,
  positionAnchoredPanel,
  resetAnchoredPanel,
  unbindAnchoredPanelReposition,
} from "../utils/anchored_panel_position";

/** Multi-select dropdown for supply chain filters: matches demo (All / one label / N selected, clear, GET submit). */
export default class extends Controller {
  static targets = ["button", "panel", "summary", "chevron", "checkbox", "clear", "wrap"];

  connect() {
    this.open = false;
    this._repositionPanel = null;
    this._onMouseDown = this._onMouseDown.bind(this);
    document.addEventListener("mousedown", this._onMouseDown);
    this.syncSummary();
    this.syncOptionStyles();
  }

  disconnect() {
    document.removeEventListener("mousedown", this._onMouseDown);
    this.unbindPanelReposition();
  }

  toggle(event) {
    event.preventDefault();
    this.open = !this.open;
    this.panelTarget.classList.toggle("hidden", !this.open);
    this.buttonTarget.setAttribute("aria-expanded", String(this.open));
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("rotate-180", this.open);
    }
    if (this.open) {
      this.positionPanel();
    } else {
      this.resetPanelPosition();
    }
  }

  clear(event) {
    event.stopPropagation();
    event.preventDefault();
    this.checkboxTargets.forEach((cb) => {
      cb.checked = false;
    });
    this.syncSummary();
    this.submitForm();
  }

  onChange() {
    this.syncOptionStyles();
    this.syncSummary();
    this.submitForm();
  }

  syncOptionStyles() {
    this.checkboxTargets.forEach((cb) => {
      const label = cb.closest("label");
      if (label) label.classList.toggle("bg-brand-25", cb.checked);
    });
  }

  syncSummary() {
    const selected = this.checkboxTargets.filter((cb) => cb.checked);
    const labels = selected.map((cb) => cb.dataset.itemLabel);
    let text;
    if (selected.length === 0) text = "All";
    else if (selected.length === 1) text = labels[0];
    else text = `${selected.length} selected`;
    this.summaryTarget.textContent = text;

    if (this.hasWrapTarget) {
      this.wrapTarget.toggleAttribute("data-active", selected.length > 0);
    }
    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("hidden", selected.length === 0);
    }
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("hidden", selected.length > 0);
    }
  }

  submitForm() {
    const form = this.element.closest("form");
    if (form) form.requestSubmit();
    else this.dispatch("changed");
  }

  _onMouseDown(event) {
    if (!this.open || this.element.contains(event.target)) return;
    this.open = false;
    this.panelTarget.classList.add("hidden");
    this.resetPanelPosition();
    this.buttonTarget.setAttribute("aria-expanded", "false");
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.remove("rotate-180");
    }
  }

  anchorElement() {
    return this.hasWrapTarget ? this.wrapTarget : this.buttonTarget;
  }

  positionPanel() {
    if (!this.hasPanelTarget) return;

    positionAnchoredPanel(this.panelTarget, this.anchorElement());
    if (this._repositionPanel) return;

    this._repositionPanel = bindAnchoredPanelReposition(
      this.panelTarget,
      this.anchorElement(),
    );
  }

  resetPanelPosition() {
    if (this.hasPanelTarget) {
      resetAnchoredPanel(this.panelTarget);
    }
    this.unbindPanelReposition();
  }

  unbindPanelReposition() {
    unbindAnchoredPanelReposition(this._repositionPanel);
    this._repositionPanel = null;
  }
}
